'use client'

import { INode } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { useDirectoryContext } from '@/features/drive/entities/directory/directory-context'
import { FileContextProvider } from '@/features/files/file-context'
import { run } from 'shared'
import { IChildrenProps } from '@/utils/typings/children-props'
import { createContext, useContext, useMemo } from 'react'
import { useDirectoryFileUploadContext } from '@/features/drive/functionalities/upload/directory-file-upload-context'
import { useDirectorySelectionContext } from '@/features/drive/functionalities/selection/directory-selection-context'
import { createSafeFallback } from '@/utils/functions/create-safe-fallback'

interface INodeItemContext {
    node: INode
    index: number
    isDisabled: boolean
    isHighlighted: boolean
    isSelected: boolean
    methods: {
        toggleSelectNode: null | ((group: boolean) => void)
    }
}

const NodeItemContext = createContext<INodeItemContext | null>(null)

interface INodeItemContextProviderProps extends IChildrenProps {
    node: INode
    index: number
    isHighlighted: boolean
}

export const NodeItemContextProvider = ({ children, node, index, isHighlighted }: INodeItemContextProviderProps) => {
    const { nodePath: directoryPath, content, disabledNodeUuids, allowFolderSelection } = useDirectoryContext()

    const {
        methods: { getIsSelected },
    } = useDirectorySelectionContext()

    const { config } = useDirectoryFileUploadContext()

    const isDisabled = useMemo(() => {
        const isDirectlyDisabled = disabledNodeUuids.includes(node.uuid)

        const accept = config.accept?.split(',').map((type) => (type.startsWith('.') ? type.slice(1) : type))

        if (accept && accept.length > 0 && node.file) {
            if (!accept.includes(node.file.type)) {
                return true
            }
        }

        if (isDirectlyDisabled) return true

        if (content.exPostFilters) {
            if (content.exPostFilters.onlyDirectories && node.type !== 'DIRECTORY') {
                return true
            }

            if (content.exPostFilters.fileCategories?.length) {
                if (node.studioProject) {
                    const hasExports = (node.studioProject.exportsCount ?? 0) > 0
                    if (!hasExports) return true
                    if (!content.exPostFilters.fileCategories.includes('VIDEO')) return true
                }

                if (node.file) {
                    const isFileCategorySelected = content.exPostFilters.fileCategories.includes(node.file.category)

                    if (!isFileCategorySelected) return true
                }
            }

            if (content.exPostFilters.fileTypes?.length && node.file) {
                const isFileTypeSelected = content.exPostFilters.fileTypes.includes(node.file.type)

                if (!isFileTypeSelected) return true
            }

            if (content.exPostFilters.disableMixedFileTypes && content.exPostFilters.selectedFileCategory) {
                if (node.studioProject && content.exPostFilters.selectedFileCategory !== 'VIDEO') {
                    return true
                }

                if (node.file && node.file.category !== content.exPostFilters.selectedFileCategory) {
                    return true
                }
            }
        }

        return false
    }, [disabledNodeUuids, node.uuid, node.type, content.exPostFilters, node.file, config.accept, node.studioProject])

    const isSelected = useMemo(
        () => !node.systemNode && getIsSelected(node.uuid, directoryPath),
        [node.systemNode, getIsSelected, node.uuid, directoryPath]
    )

    const { toggleSelectNode } = useToggleSelectNode({ node, isSelected, allowFolderSelection })

    return (
        <NodeItemContext.Provider
            value={{
                node,
                index,
                isDisabled,
                isHighlighted,
                isSelected,
                methods: {
                    toggleSelectNode,
                },
            }}
        >
            {run(() => {
                switch (typeof node.file) {
                    case 'object':
                        return <FileContextProvider file={node.file}>{children}</FileContextProvider>

                    default:
                        return children
                }
            })}
        </NodeItemContext.Provider>
    )
}

export function useNodeItemContext(): INodeItemContext
export function useNodeItemContext(opts: { strict: true }): INodeItemContext
export function useNodeItemContext(opts: { strict: false }): INodeItemContext | null
export function useNodeItemContext(opts?: { strict?: boolean }): INodeItemContext | null {
    const context = useContext(NodeItemContext)
    const isStrict = opts?.strict ?? true

    if (!context && isStrict) {
        if (process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production') {
            throw new Error('NodeItemContext not found')
        }

        console.error('NodeItemContext not found')

        return createSafeFallback({}) as INodeItemContext
    }

    return context
}

const useToggleSelectNode = ({
    node,
    isSelected,
    allowFolderSelection,
}: {
    node: INode
    isSelected: boolean
    allowFolderSelection: boolean
}) => {
    const {
        state: { maxSelectCount, selectedCount },
        methods: { toggleUuid },
    } = useDirectorySelectionContext()

    const toggleSelectNode = useMemo(() => {
        if (node.systemNode) return null

        if (!allowFolderSelection && node.type === 'DIRECTORY') return null

        if (typeof maxSelectCount === 'number') {
            if (selectedCount >= maxSelectCount && !isSelected) {
                return null
            }
        }

        return (group: boolean) => {
            toggleUuid(node.uuid, !isSelected, group)
        }
    }, [node.systemNode, node.type, node.uuid, isSelected, toggleUuid, selectedCount, maxSelectCount, allowFolderSelection])

    return {
        toggleSelectNode,
    }
}
